import * as m from '@/paraglide/messages';
import { SchemaUtils } from '@listening/api-contract/schema-utils';
import { Schema } from 'effect';
import { Button } from '@listening/frontend/components/radix/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@listening/frontend/components/radix/form';
import { Input } from '@listening/frontend/components/radix/input';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Check, Group, Headset, ShieldCheck } from 'iconoir-react';
import { callApiPromise } from '@/runtimes/get-runtime';

// Mirrors SchemaUtils.E164Phone in the API contract, which mirrors the rule
// Chatwoot enforces on Contact#phone_number. Keep the three in step — a lead
// that passes here must not be rejected downstream.
const E164_PHONE = /^\+[1-9]\d{1,14}$/;

// People type `+1 (555) 123-4567`. Drop the separators, never the leading `+`:
// stripping it (the old `\D` strip did) guaranteed every lead failed E.164.
const normalizePhone = (value: string) => value.replaceAll(/[\s().-]/g, '');

// Phone is optional — Chatwoot allows a blank `phone_number`, and demanding a
// country code on a lead form costs more leads than it captures. Validate only
// when the visitor actually typed something.
const formSchema = Schema.Struct({
  email: SchemaUtils.Email.annotate({
    message: m.business_form_email_invalid(),
  }),
  phone: Schema.String.check(
    Schema.makeFilter((val) =>
      val.trim() === '' || E164_PHONE.test(normalizePhone(val))
        ? undefined
        : m.business_form_phone_invalid(),
    ),
  ),
});

type FormValues = typeof formSchema.Type;

const benefits = [
  {
    title: m.business_benefit_support_title(),
    description: m.business_benefit_support_description(),
    icon: Headset,
  },
  {
    title: m.business_benefit_guarantees_title(),
    description: m.business_benefit_guarantees_description(),
    icon: ShieldCheck,
  },
  {
    title: m.business_benefit_account_title(),
    description: m.business_benefit_account_description(),
    icon: Group,
  },
];

export function BusinessContactForm() {
  const form = useForm<FormValues>({
    resolver: standardSchemaResolver(Schema.toStandardSchemaV1(formSchema)),
    defaultValues: { email: '', phone: '' },
  });

  const submitMutation = useMutation({
    mutationFn: (data: FormValues) =>
      callApiPromise((api) =>
        api.BusinessContact.Submit({
          payload: {
            email: data.email,
            // Omit rather than send '' — the contract requires E.164 when present.
            ...(data.phone.trim() === ''
              ? {}
              : { phone: normalizePhone(data.phone) }),
          },
        }),
      ),
    onSuccess: () => form.reset(),
  });

  const onSubmit = (data: FormValues) => submitMutation.mutate(data);

  if (submitMutation.isSuccess) {
    return (
      <section className='py-16 sm:py-24'>
        <div className='mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8'>
          <div className='mx-auto max-w-xl rounded-2xl bg-green-50 p-8 text-center'>
            <div className='mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-green-100'>
              <Check className='size-8 text-green-600' />
            </div>
            <h3 className='text-xl font-semibold text-green-800'>
              {m.business_form_success_title()}
            </h3>
            <p className='mt-2 text-green-700'>
              {m.business_form_success_message()}
            </p>
            <Button
              className='mt-6 rounded-full bg-green-600 px-6 py-2 text-white hover:bg-green-700'
              onClick={() => submitMutation.reset()}
              type='button'
            >
              {m.business_form_success_submit_another()}
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className='py-16 sm:py-24' id='contact-form'>
      <div className='mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8'>
        <div className='grid gap-8 lg:grid-cols-2 lg:gap-12'>
          <div className='rounded-2xl border border-gray-200 bg-white p-8 shadow-sm sm:p-10'>
            <h2 className='text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl'>
              {m.business_form_title()}
            </h2>
            <p className='mt-4 text-lg text-gray-600'>
              {m.business_form_subtitle()}
            </p>

            <Form {...form}>
              <form
                className='mt-8 space-y-6'
                onSubmit={form.handleSubmit(onSubmit)}
              >
                <FormField
                  control={form.control}
                  name='email'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className='text-base font-medium text-gray-900'>
                        {m.business_form_email_label()}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          autoComplete='email'
                          className='h-12 rounded-lg border border-gray-300 bg-white px-4 text-base'
                          placeholder={m.business_form_email_placeholder()}
                          type='email'
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name='phone'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className='text-base font-medium text-gray-900'>
                        {m.business_form_phone_label()}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          autoComplete='tel'
                          className='h-12 rounded-lg border border-gray-300 bg-white px-4 text-base'
                          placeholder={m.business_form_phone_placeholder()}
                          type='tel'
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  className='w-full rounded-full bg-royal-50 py-6 text-lg font-medium text-white hover:bg-royal-50/90 disabled:opacity-50'
                  disabled={submitMutation.isPending}
                  type='submit'
                >
                  {submitMutation.isPending
                    ? m.business_form_submitting()
                    : m.business_form_submit()}
                </Button>

                {submitMutation.isError && (
                  <p className='text-center text-sm text-red-600'>
                    {m.business_form_error()}
                  </p>
                )}
              </form>
            </Form>
          </div>

          <div className='relative flex flex-col justify-between overflow-hidden rounded-2xl border border-gray-200 bg-gray-50 p-8 sm:p-10'>
            <div className='absolute -top-24 -right-24 h-64 w-64 rounded-full bg-blue-100/50 blur-3xl' />
            <div className='absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-blue-50/50 blur-3xl' />

            <div className='relative'>
              <h3 className='text-2xl font-bold text-gray-900 sm:text-3xl'>
                {m.business_partner_title()}
              </h3>
              <p className='mt-2 text-gray-600'>
                {m.business_partner_subtitle()}
              </p>
              <div className='mt-8 space-y-8'>
                {benefits.map((benefit) => (
                  <div key={benefit.title} className='flex gap-5'>
                    <div className='flex size-12 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-royal-50'>
                      <benefit.icon className='size-6' />
                    </div>
                    <div>
                      <h4 className='text-lg font-bold text-gray-900'>
                        {benefit.title}
                      </h4>
                      <p className='mt-1 leading-relaxed text-gray-600'>
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className='mt-10 border-t border-gray-200 pt-8'>
                <p className='text-sm font-medium text-gray-500'>
                  {m.business_partner_trusted()}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
